/**
 * ONE TABLE ENGINE — WP-37 / MR-15, and design_system.md §9.
 *
 * ═══════════════ WHAT WAS WRONG ═══════════════
 * Six implementations of one job. Four screens — Buildings, Portfolios, Users
 * and Staff Access — drew their rows as percentage-width flexbox `<div>`s with
 * no `<table>`, no `role="table"`, no `scope`, no `aria-sort`; they are
 * copy-paste siblings that have already drifted in gap, padding, min-height and
 * overflow (`DS-CROSS-02`, `DS-CROSS-35`). Units and Residents each use a real
 * table, but by two separate private implementations. `DataTable` in
 * chart.styled.tsx is a seventh.
 *
 * ═══════════════ THE MOBILE CONTRACT, WHICH HAD TO BE SETTLED FIRST ═══════════
 * The plan warns that bolting `role="row"`/`role="cell"` onto a card reflow
 * would be "actively wrong". `UX_AUDIT_SCREENS.md:1151` says the opposite — add
 * the roles. BOTH ARE HALF RIGHT, and the evidence settles it:
 *
 *   · Below `md` these tables become `display: block`, which STRIPS a table's
 *     implicit ARIA roles. Declaring them explicitly is the only way the rows
 *     and cells stay related at all. Units already does this
 *     (dashboard-units.styled.tsx:1354-1367).
 *   · But Units also does `Thead { display: none }` below `md`, and THAT is
 *     what makes the plan's warning true: `display: none` removes the header
 *     cells from the accessibility tree, so what is left is a table whose cells
 *     have no column names — worse than a list, because it promises header
 *     association and then has none.
 *   · The replacement was CSS generated content (`::before { content:
 *     attr(data-label) }`), which is not in the DOM and is unreliably exposed —
 *     VoiceOver ignores it. So on a phone the whole inventory reads as
 *     "7A, Apartment, 3, 850, Occupied" with nothing saying which is which.
 *
 * SO: the roles are declared AND the header row is CLIPPED rather than hidden.
 * Clipping keeps the `<th>`-to-`<td>` association native and costs no layout —
 * exactly what `dashboard-tenants.tsx:449` already reasoned its way to during a
 * different package. That file is the reference; this module generalises it.
 *
 * The `data-label` generated content stays, purely as the VISUAL label on the
 * reflowed card. It is decoration now, not the accessibility story.
 *
 * ═══════════════ AND IT SCROLLS RATHER THAN CLIPS ═══════════════
 * design_system.md:304 — "Wide tables that stay tabular must scroll in an
 * `overflow-x: auto` wrapper — never clip (the old `overflow-x: hidden` bug)."
 * `TableFrame` was `overflow: hidden` on the Residents screen, which is why
 * WP-31 measured 651px of table inside a 461px frame at 768px with the content
 * simply cut off. It scrolls now, and the scroller is the frame itself so the
 * rounded border still clips the CORNERS.
 */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  palette,
  radius,
  shadow,
  spacing,
  typography,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { duration, easing } from '../../style-utils/theme';
import { listItemMotion, m } from '../../motion';

/* ── the frame ─────────────────────────────────────────────────────────── */

/**
 * The bordered card the table sits in, and the horizontal scroller.
 *
 * OVERFLOW-X AUTO, NOT HIDDEN. A table wider than its frame must be reachable;
 * clipping it removes columns from the reader with no indication that they were
 * ever there. Below `md` the rows are cards and there is nothing to scroll, so
 * the scroller is switched off rather than left to produce a stray scrollbar.
 */
export const TableFrame = styled.div`
  width: 100%;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};

  /* OVERFLOW VISIBLE, AND THAT IS A MEASURED DECISION, NOT A DEFAULT.
     The first cut used overflow-x: auto, reading design_system.md:304 ("wide
     tables must scroll, never clip"). But overflow-x: auto establishes a
     CLIPPING CONTEXT on both axes, and every one of these screens has a row
     action menu inside a cell. Measured on the LAST row, at 1440px: the menu
     extended 45px past the bottom of the frame on Users, 69px on Buildings and
     213px on Portfolios. A dropdown you have to scroll a table to see is worse
     than the problem the scroller was insuring against.

     And the insurance was buying nothing. These tables SHRINK rather than
     staying wide — percentage column widths plus overflow-wrap on the cells —
     so no horizontal page scroll was measurable at 320, 375, 768 or 1440 on any
     of the six screens with or without the scroller. The design system's rule is
     about tables that STAY tabular and wide; these reflow to cards under md.

     So: nothing is clipped, which is the rule that mattered, and the menus are
     free. Residents old overflow-hidden — the 768px clip WP-31 measured — is
     still fixed, because hidden is what it is not.
     NO BACKTICKS IN THIS COMMENT - styled template literal. */
  overflow: visible;
`;

/* The card reflow. Below md for every table; below lg as well for a table
   marked data-stack="lg" — one whose column count cannot fit a tablet-width
   column (the desk's visitor-pass register on an iPad, where eight columns left
   the visitor's name 49px wide). An ATTRIBUTE, not a component selector. */
const stackedTable = css`
  /* The roles below are what keep this a table for assistive technology once
     display has stripped the implicit ones. */
  display: block;

  /* CLIPPED, NOT display:none — see the header of this file. This is the one
     line that decides whether a phone reader gets column names at all. */
  thead {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  tbody,
  tr,
  td {
    display: block;
  }

  tr {
    padding: ${spacing.sm};
    border-bottom: 1px solid ${palette.line};
  }

  tr:last-child {
    border-bottom: none;
  }
`;

const TableBase = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${typography.ui};
  color: ${palette.ink};

  ${down('md')} {
    ${stackedTable}
  }

  ${down('lg')} {
    &[data-stack='lg'] {
      ${stackedTable}
    }
  }
`;

const TheadBase = styled.thead``;
const TbodyBase = styled.tbody``;
const TrBase = styled.tr``;

/* ── the roles that survive the reflow ─────────────────────────────────────
   Declared as defaults on wrappers rather than left to every call site, because
   "remember to add role='row' on every screen" is exactly how six
   implementations drifted apart in the first place. A caller may still override
   one deliberately. */

export const Table: React.FC<React.ComponentProps<typeof TableBase>> = ({
  role = 'table',
  ...rest
}) => <TableBase role={role} {...rest} />;

export const Thead: React.FC<React.ComponentProps<typeof TheadBase>> = ({
  role = 'rowgroup',
  ...rest
}) => <TheadBase role={role} {...rest} />;

export const Tbody: React.FC<React.ComponentProps<typeof TbodyBase>> = ({
  role = 'rowgroup',
  ...rest
}) => <TbodyBase role={role} {...rest} />;

export const Tr: React.FC<React.ComponentProps<typeof TrBase>> = ({
  role = 'row',
  ...rest
}) => <TrBase role={role} {...rest} />;

/* ── cells ─────────────────────────────────────────────────────────────── */

/** Header cell. `scope="col"` is the default because a header that does not say
 *  what it heads is the defect this engine exists to end; records-tab.tsx:522 is
 *  the only place in the app that was already getting it right. */
const ThBase = styled.th<{ numeric?: boolean; width?: number }>`
  padding: ${spacing.xs} ${spacing.sm};
  width: ${({ width }) => (width ? `${width}%` : 'auto')};
  text-align: ${({ numeric }) => (numeric ? 'right' : 'left')};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.wide};
  text-transform: uppercase;
  color: ${palette.slate};
  border-bottom: 1px solid ${palette.line};

  /* NOT nowrap + ellipsis (MR-23). The Buildings header put "Action" in a 6%
     column and ellipsised it to "Ac..." with no title and no other exposure of
     the word. A header wraps instead: a two-line header costs one row of height
     once, and a truncated one costs the reader the column's name every visit.

     BETWEEN WORDS, NEVER INSIDE ONE (2026-09-24). This was overflow-wrap:
     anywhere, and anywhere also lowers the cell's MIN-CONTENT to one letter, so
     the auto table layout was free to give the column one letter's width: at
     1280 on Buildings and Users the header read "Actio" over "n" in a 76px
     column. normal keeps every header word whole, which makes the longest word
     the column's floor - "Property name" may take two lines, "Action" one.
     NO BACKTICKS IN THIS COMMENT - styled template literal. */
  overflow-wrap: normal;
  word-break: normal;
`;

export const Th: React.FC<React.ComponentProps<typeof ThBase>> = ({
  scope = 'col',
  ...rest
}) => <ThBase scope={scope} {...rest} />;

/* A body cell on a reflowed card: its column name on the left, its value on
   the right (see stackedTable). */
const stackedCell = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  min-height: 44px;
  padding: ${spacing.xs} 0;
  border-bottom: none;
  text-align: right;
  overflow-wrap: anywhere;

  &::before {
    content: attr(data-label);
    flex: 0 0 auto;
    color: ${palette.slateLight};
    font-size: ${typography.size.xxs}px;
    font-weight: ${typography.weight.semibold};
    letter-spacing: ${typography.tracking.wide};
    text-transform: uppercase;
  }

  /* A cell with no label prints no empty gutter. */
  &:not([data-label])::before {
    content: none;
  }
`;

/**
 * Body cell.
 *
 * `data-label` is the VISUAL label on the reflowed card and nothing more — the
 * accessibility story is the clipped header row above, which keeps the native
 * association. Generated content is not in the DOM and VoiceOver ignores it, so
 * it must never be the only place a column name exists.
 */
export const Td = styled.td<{ numeric?: boolean }>`
  /* POSITIONED, SO A ROW MENU ANCHORS TO ITS OWN CELL.
     Measured, not guessed: with a static cell the Users row menu resolved its
     containing block somewhere further up and opened 40px past the right edge
     of the frame — and the frame is a scroller, so that 40px was clipped. The
     old flex-div cell happened not to clip because nothing above it scrolled.
     Anchoring to the cell keeps the menu inside the frame horizontally.
     NO BACKTICKS IN THIS COMMENT - styled template literal. */
  position: relative;
  padding: ${spacing.sm};
  text-align: ${({ numeric }) => (numeric ? 'right' : 'left')};
  font-variant-numeric: ${({ numeric }) => (numeric ? 'tabular-nums' : 'normal')};
  color: ${palette.ink};
  border-bottom: 1px solid ${palette.line};
  vertical-align: middle;

  ${down('md')} {
    ${stackedCell}
  }

  ${down('lg')} {
    table[data-stack='lg'] & {
      ${stackedCell}
    }
  }
`;

/**
 * The body row: resting and hover treatment per design_system.md §9, and the
 * cascade-in that section also asks for.
 *
 * A MOTION ROW, NOT A KEYFRAME. The row is `m.tr` carrying `listItemMotion`
 * (src/motion/stagger.ts): it arrives with the shared row variants, its delay
 * comes from `index` (capped at STAGGER.maxItems, so a long page does not spend
 * a second arriving), `layout` slides the survivors into place when a sibling
 * leaves, and inside a `Presence` a removed row animates OUT — which a CSS
 * keyframe could never do, because the element is gone before it starts.
 *
 * THE STACKING TRAP STAYS FIXED. The old keyframe had to run `backwards`, not
 * `both`, because a fill-mode transform left on the row forever creates a
 * stacking context that traps an open row menu's z-index under the next row
 * (Buildings carried a position/z-index workaround for exactly that). Framer
 * writes `transform: none` once x/y are back at rest, so no residual transform
 * exists and no screen needs the workaround.
 *
 * Reduced motion is honoured by MotionRoot (MotionConfig reducedMotion="user"):
 * the travel is skipped and the fade kept, so a row is never simply late.
 */
const BodyRowBase = m.tr`
  background: ${palette.surface};

  td {
    /* duration.fast, not a hand-written 120ms. Every cell of every table
       carried the off-scale value, which made it 4,685 of the audit's 4,735
       motion-duration flags on its own. */
    transition: background-color ${duration.fast} ${easing.standard};
  }

  &:hover td {
    background: ${palette.irisTintSoft};
  }

  &:last-child td {
    border-bottom: none;
  }
`;

export type BodyRowProps = React.ComponentProps<typeof BodyRowBase> & {
  /** The row's position on its page — drives the cascade delay. */
  index?: number;
};

export const BodyRow = React.forwardRef<HTMLTableRowElement, BodyRowProps>(
  ({ index = 0, role = 'row', ...rest }, ref) => (
    <BodyRowBase ref={ref} role={role} {...listItemMotion(index)} {...rest} />
  )
);

BodyRow.displayName = 'BodyRow';

/**
 * A cell whose content is a row of things rather than one value — an avatar
 * beside a name, a pill beside a count.
 *
 * A `<td>` is `display: table-cell`, so the flex layout the old `<div>` rows got
 * for free has to be asked for. Kept in the engine rather than re-declared per
 * screen: "the cell that holds an avatar and a name" is the same cell on four
 * of them.
 */
export const CellFlex = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
  max-width: 100%;
`;

/**
 * The table's accessible name.
 *
 * A `<caption>` rather than `aria-label`, because it is the element the spec
 * gives a table for exactly this and it survives being read out of context in a
 * list of landmarks. Visually hidden: these tables all sit under a heading that
 * already says the same thing to a sighted reader.
 */
export const TableCaption = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
